import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/auth/useAuth";
import { Star } from "lucide-react";
import { cn } from "@/lib/utils";
import type { FeedbackContext } from "./FeedbackButton";

type FeedbackType = "feature" | "bug" | "rating";
type SeverityLevel = "minor" | "significant" | "blocking";

interface FeedbackFormProps {
  /** Called after a successful submission so parent (e.g. Dialog) can close. */
  onSuccess?: () => void;
  /** Page context for auto-filling pattern/stitch ratings */
  context?: FeedbackContext;
}

export function FeedbackForm({ onSuccess, context }: FeedbackFormProps) {
  // Auto-select "rating" type when context has a pattern/stitch
  const defaultType: FeedbackType = context?.contextType ? "rating" : "feature";

  const [feedbackType, setFeedbackType] = useState<FeedbackType>(defaultType);
  const [feedback, setFeedback] = useState("");
  const [rating, setRating] = useState<number>(0);
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [severity, setSeverity] = useState<SeverityLevel | "">("");
  const [resolvedContextName, setResolvedContextName] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  // Resolve the pattern or stitch name when context is available
  useEffect(() => {
    if (!context?.contextType || !context?.contextId) return;

    const fetchName = async () => {
      if (context.contextType === 'pattern') {
        const { data } = await supabase
          .from('patterns')
          .select('name')
          .eq('id', context.contextId)
          .single();
        setResolvedContextName(data?.name ?? null);
      } else if (context.contextType === 'stitch') {
        const { data } = await supabase
          .from('stitches')
          .select('name')
          .eq('id', Number(context.contextId))
          .single();
        setResolvedContextName(data?.name ?? null);
      }
    };

    fetchName();
  }, [context?.contextType, context?.contextId]);

  // Reset rating and severity when switching types
  useEffect(() => {
    if (feedbackType !== "rating") {
      setRating(0);
      setHoverRating(0);
    }
    if (feedbackType !== "bug") {
      setSeverity("");
    }
  }, [feedbackType]);

  const getContentLabel = () => {
    switch (feedbackType) {
      case "feature": return "Describe the feature you'd like to see";
      case "bug": return "Describe the issue you encountered";
      case "rating": return "What worked well? What didn't?";
    }
  };

  const getContentPlaceholder = () => {
    switch (feedbackType) {
      case "feature": return "I would like to see...";
      case "bug": return "I encountered an issue when...";
      case "rating": return "The pattern was clear and easy to follow, but...";
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (feedback.trim().length === 0) {
      toast({
        title: "Error",
        description: "Please provide details about your feedback.",
        variant: "destructive",
      });
      return;
    }

    if (feedbackType === "rating" && rating === 0) {
      toast({
        title: "Error",
        description: "Please select a star rating.",
        variant: "destructive",
      });
      return;
    }

    if (feedbackType === "bug" && !severity) {
      toast({
        title: "Error",
        description: "Please select how severe this issue is.",
        variant: "destructive",
      });
      return;
    }

    if (!user) {
      toast({
        title: "Authentication Error",
        description: "You must be logged in to submit feedback.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      // Auto-map bug severity to an initial priority for admin triage
      const severityToPriority: Record<SeverityLevel, string> = {
        blocking: 'high',
        significant: 'medium',
        minor: 'low',
      };

      const insertData: Record<string, string | number | null> = {
        user_id: user.id,
        feedback_type: feedbackType,
        feedback_content: feedback.trim(),
        page_context: context?.pageUrl || null,
      };

      // Save context linkage for ALL feedback types when on a pattern/stitch page
      if (context?.contextType === 'pattern' && context.contextId) {
        insertData.related_pattern_id = context.contextId;
      }
      if (context?.contextType === 'stitch' && context.contextId) {
        insertData.related_stitch_id = Number(context.contextId);
      }

      // Rating-specific fields
      if (feedbackType === "rating") {
        insertData.rating = rating;
      }

      // Bug-specific fields
      if (feedbackType === "bug" && severity) {
        insertData.user_severity = severity;
        insertData.priority = severityToPriority[severity];
      }

      const { error } = await supabase
        .from('beta_feedback')
        .insert(insertData);

      if (error) throw error;

      toast({
        title: "Feedback submitted",
        description: "Thank you! Our team will review it shortly.",
      });

      setFeedback("");
      setRating(0);
      setSeverity("");
      onSuccess?.();
    } catch (error) {
      console.error("Error submitting feedback:", error instanceof Error ? error.message : error);
      toast({
        title: "Submission failed",
        description: "There was an error submitting your feedback. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Show what the feedback is linked to when on a pattern/stitch page */}
      {context?.contextType && (
        <div className="rounded-md bg-primary/5 border border-primary/20 px-3 py-2 text-sm">
          <span className="text-muted-foreground">Feedback about: </span>
          <span className="font-medium text-foreground">
            {resolvedContextName ?? (context.contextType === 'pattern' ? 'this pattern' : 'this stitch')}
          </span>
        </div>
      )}

      <div className="space-y-2">
        <Label>Feedback Type</Label>
        <RadioGroup
          value={feedbackType}
          onValueChange={(value) => setFeedbackType(value as FeedbackType)}
          className="flex flex-col space-y-1"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="feature" id="fb-feature" />
            <Label htmlFor="fb-feature" className="cursor-pointer font-normal">Feature Request</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="bug" id="fb-bug" />
            <Label htmlFor="fb-bug" className="cursor-pointer font-normal">Bug Report</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="rating" id="fb-rating" />
            <Label htmlFor="fb-rating" className="cursor-pointer font-normal">Pattern / Stitch Rating</Label>
          </div>
        </RadioGroup>
      </div>

      {/* Star rating — only for rating type */}
      {feedbackType === "rating" && (
        <div className="space-y-2">
          <Label>Rating</Label>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                className="p-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
                aria-label={`${star} star${star > 1 ? 's' : ''}`}
              >
                <Star
                  size={24}
                  className={cn(
                    'transition-colors',
                    (hoverRating || rating) >= star
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-gray-300'
                  )}
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Severity — only for bug type */}
      {feedbackType === "bug" && (
        <div className="space-y-2">
          <Label htmlFor="fb-severity">How severe is this issue?</Label>
          <Select value={severity} onValueChange={(v) => setSeverity(v as SeverityLevel)}>
            <SelectTrigger id="fb-severity">
              <SelectValue placeholder="Select severity..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="minor">Minor inconvenience</SelectItem>
              <SelectItem value="significant">Significant issue</SelectItem>
              <SelectItem value="blocking">Completely blocking</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="fb-content">{getContentLabel()}</Label>
        <Textarea
          id="fb-content"
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder={getContentPlaceholder()}
          className="min-h-[120px]"
        />
        {feedbackType === "bug" && (
          <p className="text-xs text-muted-foreground">
            Include: steps to reproduce, expected behaviour, and what actually happened.
          </p>
        )}
      </div>

      <div className="rounded-md bg-muted/50 border border-border p-3 text-xs text-muted-foreground">
        Your feedback is linked to your account so our team can follow up if needed.
        Only administrators can view submissions.
      </div>

      <Button type="submit" disabled={isSubmitting} className="w-full">
        {isSubmitting ? "Submitting\u2026" : "Submit Feedback"}
      </Button>
    </form>
  );
}
